/**
 * Maps source numbers to human-readable text
 * @param {number|string} source - Source number (1, 2, 3)
 * @returns {string} Human-readable source text
 */
export const getSourceLabel = (source) => {
  const sourceNum = typeof source === 'string' ? parseInt(source, 10) : source;

  switch (sourceNum) {
    case 1:
      return 'Email';
    case 2:
      return 'Telefonsamtal';
    case 3:
      return 'Landningsida';
    default:
      return 'Okänd källa';
  }
};

/**
 * Gets a color for the source type
 * @param {number|string} source - Source number (1, 2, 3)
 * @returns {string} Color for the source
 */
export const getSourceColor = (source) => {
  const sourceNum = typeof source === 'string' ? parseInt(source, 10) : source;

  switch (sourceNum) {
    case 1:
      return 'warning'; // Email - orange
    case 2:
      return 'success'; // Telefonsamtal - green
    case 3:
      return 'primary'; // Landningsida - blue
    default:
      return 'default';
  }
};

/**
 * Gets an icon for the source type
 * @param {number|string} source - Source number (1, 2, 3)
 * @returns {string} Icon name for the source
 */
export const getSourceIcon = (source) => {
  const sourceNum = typeof source === 'string' ? parseInt(source, 10) : source;

  switch (sourceNum) {
    case 1:
      return 'Email'; // Email
    case 2:
      return 'Phone'; // Telefonsamtal
    case 3:
      return 'Web'; // Landningsida
    default:
      return 'HelpOutline';
  }
};

/**
 * Formats a phone number for display
 * @param {string} phoneNumber - Raw phone number
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove any non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // Swedish phone number formatting
  if (digits.startsWith('46')) {
    // Swedish international format (+46)
    if (digits.length === 11) {
      return `+46 (0) ${digits.slice(2, 4)}-${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`;
    }
    if (digits.length === 12) {
      return `+46 (0) ${digits.slice(2, 5)}-${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10)}`;
    }
    return `+46 (0) ${digits.slice(2, 4)}-${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
  }

  // Swedish national format (0-prefix)
  if (digits.startsWith('0') && digits.length >= 9) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  // Default formatting - just add spaces every 3 digits
  return digits.replace(/(\d{3})(?=\d)/g, '$1 ');
};

/**
 * Gets contact information based on source type
 * @param {Object} lead - Lead object
 * @returns {Object} Object with primary and secondary contact info
 */
export const getContactInfo = (lead) => {
  const sourceNum = typeof lead.source === 'string' ? parseInt(lead.source, 10) : lead.source;

  switch (sourceNum) {
    case 1: // Email
      return {
        primary: lead.sendersEmail || 'Okänd e-post',
        secondary: lead.senderName || null,
      };

    case 2: // Telefonsamtal
      {
        // Parse data if it's a string
        let data = lead.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            data = {};
          }
        }

        // Extract Caller ID (note the double space in the international variant)
        const callerId = data?.['Caller ID'] || data?.['Caller ID  (international)'];

        if (callerId) {
          // Format phone number for display
          const formattedNumber = formatPhoneNumber(callerId);
          return {
            primary: formattedNumber,
            secondary: null, // Don't show secondary text for phone numbers
          };
        }

        return {
          primary: 'Okänt telefonnummer',
          secondary: null, // Don't show secondary text for phone numbers
        };
      }

    case 3: // Landningsida
      return {
        primary: lead.sendersEmail || 'Okänd kontakt',
        secondary: lead.senderName || null,
      };

    default:
      return {
        primary: lead.sendersEmail || 'Okänd kontakt',
        secondary: lead.senderName || null,
      };
  }
};

/**
 * Gets detailed contact information for display in contact info section
 * @param {Object} lead - Lead object
 * @returns {Array} Array of contact information objects
 */
export const getDetailedContactInfo = (lead) => {
  const sourceNum = typeof lead.source === 'string' ? parseInt(lead.source, 10) : lead.source;
  const contactInfoItems = [];

  switch (sourceNum) {
    case 1: // Email
      if (lead.sendersEmail) {
        contactInfoItems.push({
          label: 'E-post',
          value: lead.sendersEmail,
          icon: 'Email',
          color: 'info',
        });
      }
      if (lead.senderName) {
        contactInfoItems.push({
          label: 'Namn',
          value: lead.senderName,
          icon: 'Person',
          color: 'primary',
        });
      }
      break;

    case 2: // Telefonsamtal
      {
        // Parse data if it's a string
        let data = lead.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            data = {};
          }
        }

        // Caller ID
        if (data?.['Caller ID']) {
          contactInfoItems.push({
            label: 'Caller ID',
            value: formatPhoneNumber(data['Caller ID']),
            icon: 'Phone',
            color: 'success',
          });
        }

        // Caller ID (international)
        if (data?.['Caller ID  (international)']) {
          contactInfoItems.push({
            label: 'Caller ID (international)',
            value: formatPhoneNumber(data['Caller ID  (international)']),
            icon: 'Phone',
            color: 'success',
          });
        }
      }
      break;

    case 3: // Landningsida
      {
        if (lead.senderName) {
          contactInfoItems.push({
            label: 'Namn',
            value: lead.senderName,
            icon: 'Person',
            color: 'primary',
          });
        }
        if (lead.sendersEmail) {
          contactInfoItems.push({
            label: 'E-post',
            value: lead.sendersEmail,
            icon: 'Email',
            color: 'info',
          });
        }

        // Parse data to look for phone number from landing page
        let data = lead.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            data = {};
          }
        }

        // Look for phone number fields from landing page forms
        const phoneFields = ['Telefonnummer', 'Telefon', 'Phone', 'Mobilnummer', 'Mobil'];
        const phoneNumber = phoneFields.find((field) => data?.[field])
          ? data[phoneFields.find((field) => data?.[field])]
          : null;

        if (phoneNumber) {
          contactInfoItems.push({
            label: 'Telefonnummer',
            value: formatPhoneNumber(phoneNumber),
            icon: 'Phone',
            color: 'success',
          });
        }
      }
      break;

    default:
      if (lead.sendersEmail) {
        contactInfoItems.push({
          label: 'E-post',
          value: lead.sendersEmail,
          icon: 'Email',
          color: 'info',
        });
      }
      if (lead.senderName) {
        contactInfoItems.push({
          label: 'Namn',
          value: lead.senderName,
          icon: 'Person',
          color: 'primary',
        });
      }
      break;
  }

  return contactInfoItems;
};

/**
 * Fixes text encoding issues from UTF-8 mojibake
 * @param {string} text - Text with potential encoding issues
 * @returns {string} Fixed text
 */
export const fixTextEncoding = (text) => {
  if (!text || typeof text !== 'string') return text;

  let fixed = text
    // Swedish letters (most common issues)
    .replace(/Ã¤/g, 'ä')
    .replace(/Ã¥/g, 'å')
    .replace(/Ã¶/g, 'ö')
    .replace(/Ã„/g, 'Ä')
    .replace(/Ã…/g, 'Å')
    .replace(/Ã–/g, 'Ö')
    // Accented letters
    .replace(/Ã©/g, 'é')
    .replace(/Ã¨/g, 'è')
    .replace(/Ãª/g, 'ê')
    .replace(/Ã«/g, 'ë')
    .replace(/Ã¡/g, 'á')
    .replace(/Ã /g, 'à')
    .replace(/Ã¢/g, 'â')
    .replace(/Ã£/g, 'ã')
    .replace(/Ã­/g, 'í')
    .replace(/Ã¬/g, 'ì')
    .replace(/Ã®/g, 'î')
    .replace(/Ã¯/g, 'ï')
    .replace(/Ã³/g, 'ó')
    .replace(/Ã²/g, 'ò')
    .replace(/Ã´/g, 'ô')
    .replace(/Ãµ/g, 'õ')
    .replace(/Ãº/g, 'ú')
    .replace(/Ã¹/g, 'ù')
    .replace(/Ã»/g, 'û')
    .replace(/Ã¼/g, 'ü')
    .replace(/Ã½/g, 'ý')
    .replace(/Ã¿/g, 'ÿ')
    .replace(/Ã±/g, 'ñ')
    .replace(/Ã§/g, 'ç')
    // Uppercase accented letters
    .replace(/Ã‰/g, 'É')
    .replace(/Ã‹/g, 'Ë')
    .replace(/Ã€/g, 'À')
    .replace(/Ã‚/g, 'Â')
    .replace(/Ãƒ/g, 'Ã')
    .replace(/Ã"/g, 'Ó')
    .replace(/Ã'/g, 'Ò')
    .replace(/Ã"/g, 'Ô')
    .replace(/Ã•/g, 'Õ')
    .replace(/Ãš/g, 'Ú')
    .replace(/Ã™/g, 'Ù')
    .replace(/Ã›/g, 'Û')
    .replace(/Ãœ/g, 'Ü')
    .replace(/Ã'/g, 'Ñ')
    .replace(/Ã‡/g, 'Ç')
    // Special characters and dashes (Windows-1252 issues)
    .replace(/â€"/g, '–') // en dash
    .replace(/â€"/g, '—') // em dash
    .replace(/â€˜/g, "'") // left single quote
    .replace(/â€™/g, "'") // right single quote / apostrophe
    .replace(/â€œ/g, '"') // left double quote
    .replace(/â€/g, '"') // right double quote
    .replace(/â€¦/g, '…') // ellipsis
    .replace(/â€¢/g, '•') // bullet
    .replace(/â‚¬/g, '€') // euro sign
    // Cleanup unwanted characters
    .replace(/Â/g, '');

  // Standalone Ã — the second UTF-8 byte was dropped.
  // When UTF-8 passes through ISO-8859-1, the second byte of uppercase
  // accented chars (C1 control range 0x80-0x9F) is silently stripped.
  // Affected Swedish chars: Ä(C3 84), Å(C3 85), Ö(C3 96).
  // Lowercase ä/å/ö are NOT affected (their second bytes 0xA4/0xA5/0xB6
  // are visible Latin-1 chars, handled as two-byte mojibake above).
  // We resolve the ambiguity using Swedish language heuristics.
  const phase1 = fixed;
  fixed = fixed.replace(/Ã(?=[A-Za-z])/g, (_match, offset) => {
    const charBefore = offset > 0 ? phase1[offset - 1] : '';
    const afterChunk = phase1.substring(offset + 1, Math.min(phase1.length, offset + 20));
    const afterLower = afterChunk.toLowerCase();
    const beforeCtx = phase1.substring(Math.max(0, offset - 5), offset);

    const isWordStart = !charBefore || /[\s\n\r.,;:!?()[\]{}"'/<>-]/.test(charBefore);
    const isAfterUpper = /[A-ZÅÄÖ]/.test(charBefore);
    const isFollowedByUpper = /^[A-Z]/.test(afterChunk);

    if (isWordStart) {
      // --- Ö word patterns ---
      if (/^st(?:er|ra|ern|erg)/.test(afterLower)) return 'Ö';
      if (/^pp/.test(afterLower)) return 'Ö';
      if (/^ver/.test(afterLower)) return 'Ö';
      if (/^vr/.test(afterLower)) return 'Ö';
      if (/^reb/.test(afterLower)) return 'Ö';
      if (/^rn[se]/.test(afterLower)) return 'Ö';
      if (/^gon/.test(afterLower)) return 'Ö';
      if (/^nsk/.test(afterLower)) return 'Ö';
      if (/^ken(?![a-zåäö])/.test(afterLower)) return 'Ö';
      if (/^de[stns]?(?![a-zåäö])/.test(afterLower)) return 'Ö';
      if (/^l(?![a-zåäö])/.test(afterLower)) return 'Ö';
      if (/^n(?![a-zåäö])/.test(afterLower)) return 'Ö';

      // --- Å word patterns ---
      if (/^ter/.test(afterLower)) return 'Å';
      if (/^tg/.test(afterLower)) return 'Å';
      if (/^rs/.test(afterLower)) return 'Å';
      if (/^ld/.test(afterLower)) return 'Å';
      if (/^ng[rea]/.test(afterLower)) return 'Å';
      if (/^ker/.test(afterLower)) return 'Å';
      if (/^ka(?![a-zåäö])/.test(afterLower)) return 'Å';
      if (/^s(?![a-zåäö])/.test(afterLower) || /^s$/.test(afterLower)) return 'Å';
      if (/^sa(?![a-zåäö])/.test(afterLower) || /^sa$/.test(afterLower)) return 'Å';

      // --- Ä word patterns ---
      if (/^r(?![a-zåäö])/.test(afterLower) || /^r$/.test(afterLower)) {
        if (/\d\s*$/.test(beforeCtx)) return 'Å';
        return 'Ä';
      }
      if (/^lsk/.test(afterLower)) return 'Ä';
      if (/^lg(?![a-zåäö])/.test(afterLower)) return 'Ä';
      if (/^lv/.test(afterLower)) return 'Ä';
      if (/^ldre/.test(afterLower)) return 'Ä';
      if (/^ndr/.test(afterLower)) return 'Ä';
      if (/^mn/.test(afterLower)) return 'Ä';
      if (/^ppl/.test(afterLower)) return 'Ä';
      if (/^ven(?![a-zåäö])/.test(afterLower) || /^ven$/.test(afterLower)) return 'Ä';
      if (/^vent/.test(afterLower)) return 'Ä';
      if (/^rend/.test(afterLower)) return 'Ä';
      if (/^ng(?![a-zåäö])/.test(afterLower) || /^ng$/.test(afterLower)) return 'Ä';
      if (/^tt/.test(afterLower)) return 'Ä';
      if (/^g(?:a|er|are|d|de|t)(?![a-zåäö])/.test(afterLower)) return 'Ä';
      if (/^nt/.test(afterLower)) return 'Ä';

      return 'Ä';
    }

    if (isAfterUpper && isFollowedByUpper) {
      const twoBefore = beforeCtx.toUpperCase().slice(-2);
      const charAfterUpper = (afterChunk[0] || '').toUpperCase();

      if (twoBefore.endsWith('BJ')) return 'Ö';

      if (charAfterUpper === 'L') {
        if (/M$/.test(twoBefore)) return 'Å';
        if (/H$/.test(twoBefore)) return 'Å';
        if (/T$/.test(twoBefore)) return 'Å';
        return 'Ä';
      }

      if (twoBefore.endsWith('G') && charAfterUpper === 'T') return 'Ö';
      if (twoBefore.endsWith('S') && charAfterUpper === 'D') return 'Ö';
      if (twoBefore.endsWith('H') && charAfterUpper === 'G') return 'Ö';
      if (twoBefore.endsWith('K') && charAfterUpper === 'P') return 'Ö';
      if (twoBefore.endsWith('S') && charAfterUpper === 'K') return 'Ö';
      if (twoBefore.endsWith('N') && charAfterUpper === 'J') return 'Ö';
      if (twoBefore.endsWith('R') && charAfterUpper === 'R') return 'Ö';
      if (twoBefore.endsWith('G') && charAfterUpper === 'R') return 'Ö';
      if (twoBefore.endsWith('F') && charAfterUpper === 'R') return 'Ö';
      if (twoBefore.endsWith('B') && charAfterUpper === 'N') return 'Ö';
      if (twoBefore.endsWith('D') && charAfterUpper === 'D') return 'Ö';
      if (twoBefore.endsWith('N') && charAfterUpper === 'D') return 'Ö';
      if (twoBefore.endsWith('V') && charAfterUpper === 'R') return 'Å';

      return 'Ä';
    }

    if (isAfterUpper) return 'Ä';

    return 'Ä';
  });

  fixed = fixed.replace(/ã([a-z])/g, 'ö$1');

  // Handle standalone â (incomplete en-dash/em-dash encoding)
  // â between word characters or around spaces/punctuation → en dash
  // Note: Avoiding lookbehind assertions for Safari compatibility
  fixed = fixed
    .replace(/\sâ\s/g, ' – ') // â surrounded by spaces
    .replace(/\sâ(?=[a-zA-Z0-9@])/g, ' – ') // â after space, before word/email
    .replace(/([a-zA-Z0-9])â\s/g, '$1 – ') // â after word/number, before space (capture group instead of lookbehind)
    .replace(/([a-zA-Z0-9])â(?=[a-zA-Z0-9@])/g, '$1 – ') // â between word chars (capture group instead of lookbehind)
    .replace(/â$/g, '–') // â at end
    .replace(/^â/g, '–'); // â at start

  return fixed;
};

/**
 * Decodes HTML entities and formats text content while preserving structure
 * @param {string} htmlString - HTML string to decode
 * @returns {string} Cleaned and formatted text with preserved line breaks
 */
export const decodeHtmlContent = (htmlString) => {
  if (!htmlString || typeof htmlString !== 'string') return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;

  const blockElements = new Set([
    'div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'li', 'blockquote', 'pre', 'section', 'article', 'header', 'footer',
    'tr', 'dt', 'dd', 'figcaption',
  ]);

  const extractTextWithBreaks = (element) => {
    let result = '';

    for (const node of element.childNodes) {
      if (node.nodeType === 3) {
        result += node.textContent;
      } else if (node.nodeType === 1) {
        const tagName = node.tagName.toLowerCase();

        if (tagName === 'br') {
          result += '\n';
        } else if (tagName === 'hr') {
          if (result && !result.endsWith('\n')) result += '\n';
          result += '---\n';
        } else if (blockElements.has(tagName)) {
          const content = extractTextWithBreaks(node);
          if (content.trim()) {
            if (result && !result.endsWith('\n')) {
              result += '\n';
            }
            if (tagName === 'li') {
              result += `• ${content.trim()}`;
            } else {
              result += content.trim();
            }
            result += '\n';
          }
        } else if (tagName === 'td' || tagName === 'th') {
          const content = extractTextWithBreaks(node);
          if (content.trim()) {
            if (result && !result.endsWith('\n') && !result.endsWith('\t')) {
              result += '\t';
            }
            result += content.trim();
          }
        } else {
          result += extractTextWithBreaks(node);
        }
      }
    }

    return result;
  };

  let textContent = extractTextWithBreaks(tempDiv);

  textContent = fixTextEncoding(textContent);

  textContent = textContent
    .replace(/&nbsp;/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .trim();

  return textContent;
};

/**
 * Checks whether a string looks like it contains HTML markup.
 * @param {string} text
 * @returns {boolean}
 */
export const containsHtml = (text) => {
  if (!text || typeof text !== 'string') return false;
  return /<\/?[a-z][\s\S]*?>/i.test(text);
};

/**
 * Converts an HTML string to readable plain text (light-weight version
 * of decodeHtmlContent for short messages / conversation entries).
 * Replaces <br> with newlines, strips remaining tags, and decodes entities.
 * @param {string} text
 * @returns {string}
 */
export const htmlToPlainText = (text) => {
  if (!text || typeof text !== 'string') return text || '';
  if (!containsHtml(text)) return text;
  return decodeHtmlContent(text);
};

/**
 * Formats the original lead message based on source type
 * @param {Object} lead - Lead object
 * @returns {Object} Formatted message object with type and content
 */
export const formatOriginalMessage = (lead) => {
  const sourceNum = typeof lead.source === 'string' ? parseInt(lead.source, 10) : lead.source;

  if (!lead.content) {
    return {
      type: 'empty',
      content: 'Inget meddelande tillgängligt',
      fields: [],
    };
  }

  switch (sourceNum) {
    case 1: // Email
      return {
        type: 'email',
        content: decodeHtmlContent(lead.content),
        fields: [],
        isHtml: true,
      };

    case 2: // Telefonsamtal
      {
        const fields = [];
        let callTranscriptionHtml = null;

        // Fix encoding issues in the raw content first
        const fixedContent = fixTextEncoding(lead.content);

        // Check if content contains Call Transcription HTML
        const transcriptionMatch = fixedContent.match(/Call Transcription:\s*\n?([\s\S]*)/i);
        let contentToProcess = fixedContent;

        if (transcriptionMatch) {
          // Extract HTML transcription content
          callTranscriptionHtml = transcriptionMatch[1].trim();
          // Only process the content before the transcription
          contentToProcess = fixedContent.substring(0, fixedContent.indexOf('Call Transcription:'));
        }

        const lines = contentToProcess.split('\n');

        lines.forEach((line) => {
          const trimmedLine = line.trim();
          if (trimmedLine && trimmedLine.includes(':')) {
            const [key, ...valueParts] = trimmedLine.split(':');
            const value = valueParts.join(':').trim();

            // Skip empty values
            if (!value) return;

            // Translate common fields to Swedish
            const fieldTranslations = {
              'Start Time': 'Starttid',
              'Tracking Number': 'Spårningsnummer',
              'Call Outcome': 'Samtalsresultat',
              'Caller ID': 'Caller ID',
              'Talk Duration': 'Samtalstid',
              'Project Name': 'Projektnamn',
            };

            const translatedKey = fieldTranslations[key.trim()] || key.trim();
            let formattedValue = value;

            // Format specific fields
            if (key.trim() === 'Talk Duration') {
              const seconds = parseInt(value, 10);
              if (!Number.isNaN(seconds)) {
                const minutes = Math.floor(seconds / 60);
                const remainingSeconds = seconds % 60;
                formattedValue = `${minutes}:${remainingSeconds.toString().padStart(2, '0')} min`;
              }
            }
            if (key.trim() === 'Call Outcome') {
              const outcomes = {
                ANSWERED: 'Besvarad',
                NO_ANSWER: 'Inget svar',
                BUSY: 'Upptaget',
                FAILED: 'Misslyckades',
              };
              formattedValue = outcomes[value] || value;
            }

            fields.push({
              label: translatedKey,
              value: formattedValue,
              key: key.trim(),
            });
          }
        });

        return {
          type: 'phone',
          content: 'Telefonsamtal',
          fields,
          callTranscriptionHtml,
        };
      }

    case 3: // Landningsida
      {
        const fields = [];

        // Try to parse lead.data directly as JSON to preserve multiline values.
        // The content string (key: value joined by \n) is lossy when values contain newlines.
        let parsedDataObj = null;
        if (lead.data) {
          try {
            parsedDataObj = typeof lead.data === 'string' ? JSON.parse(lead.data) : lead.data;
          } catch (e) {
            parsedDataObj = null;
          }
        }

        if (parsedDataObj && typeof parsedDataObj === 'object') {
          Object.entries(parsedDataObj).forEach(([key, value]) => {
            if (key === 'source_type') return;
            if (key && value != null && String(value).trim()) {
              fields.push({
                label: key,
                value: fixTextEncoding(String(value).trim()),
                key: key.toLowerCase(),
              });
            }
          });
        } else {
          // Fallback: parse the content string when lead.data is unavailable.
          // Lines without a "key: value" pattern are treated as continuations
          // of the preceding field so multiline values are preserved.
          const fixedContent = fixTextEncoding(lead.content);
          const lines = fixedContent.split('\n');

          const keyValuePattern = /^([^:]+):\s*(.*)/;

          // A line counts as a new field when it matches "Key: value" AND the
          // key part looks like a label (not a URL, not a bare number/time).
          const looksLikeFieldKey = (key) => {
            if (!key) return false;
            if (/^\d/.test(key)) return false;
            if (/^https?$/.test(key.toLowerCase())) return false;
            return true;
          };

          let currentKey = null;
          let currentValueLines = [];

          const flushField = () => {
            if (currentKey !== null) {
              const value = currentValueLines.join('\n');
              if (value) {
                fields.push({
                  label: currentKey,
                  value,
                  key: currentKey.toLowerCase(),
                });
              }
            }
          };

          for (let i = 0; i < lines.length; i += 1) {
            const trimmedLine = lines[i].trim();
            if (trimmedLine) {
              const match = trimmedLine.match(keyValuePattern);

              if (match && looksLikeFieldKey(match[1].trim())) {
                flushField();
                currentKey = match[1].trim();
                currentValueLines = match[2].trim() ? [match[2].trim()] : [];
              } else if (currentKey !== null) {
                currentValueLines.push(trimmedLine);
              } else {
                currentKey = 'Meddelande';
                currentValueLines = [trimmedLine];
              }
            }
          }

          flushField();
        }

        return {
          type: 'landing',
          content: 'Förfrågan från landningsida',
          fields,
        };
      }

    default:
      return {
        type: 'text',
        content: fixTextEncoding(lead.content),
        fields: [],
      };
  }
};
